import { useMobile } from "hooks/Mobile/useMobile";
import { SWRDataGrid } from "./SWRDataGrid";
import { GridPaginationProps, filterPanelSx, formatDateTime } from ".";
import { GridColDef, GridColumnMenuItemProps, useGridApiContext } from "@mui/x-data-grid-pro";
import { IBuildUpPlayer, IBuildUpStatus } from "services/models/BuildUpPlayer";
import { useBuildUpPlayersOnFloor, useBuildUpStatuses } from "services/api/SWRService";
import { Box, Grid, IconButton, Link, ListItemText, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { PLAYER_NAME_MIN_WIDTH } from "constants/tables";
import Routes from "constants/routes";
import { toCurrencyFormat } from "utils/string-utils";
import { SMTooltip } from "components/lib/SMTooltip";
import HdrAutoIcon from "@mui/icons-material/HdrAuto";
import { CustomColumnMenu } from "components/Dashboard/Tables/CustomColumnMenu";
import { memo } from "react";
import { ErrorBoundary } from "components/ErrorBoundary";
import { useCanAccess } from "@sanmanuel/permissions";
import { AppPermissions } from "constants/permissions";
import { Outlet, Link as RouterLink, useLocation } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";
import { ClickableTypography } from "../ClickableTypography";
import { OptionsSkeleton } from "components/BuildUp/OptionsSkeleton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

declare module "@mui/x-data-grid" {
  interface ColumnMenuPropsOverrides {
    customizedcolumns: string[];
    customfilteritem: React.MemoExoticComponent<(props: GridColumnMenuItemProps) => JSX.Element>;
  }
}

const CustomBuildUpStatusFilterItem = memo((props: GridColumnMenuItemProps): JSX.Element => {
  const { data: statusOptions, error, mutate } = useBuildUpStatuses();
  const apiRef = useGridApiContext();

  if (!statusOptions) return <OptionsSkeleton />;

  const handleClick = (filterType: IBuildUpStatus): void => {
    const match = statusOptions.find((o) => o.incidentStatusId === filterType.incidentStatusId);

    if (match) {
      apiRef.current.setFilterModel(
        {
          items: [
            {
              field: "status",
              value: match.statusName,
              operator: "equals",
            },
          ],
        },
        "changeLogicOperator"
      );
    } else {
      apiRef.current.setFilterModel(
        {
          items: [],
        },
        "deleteFilterItem"
      );
    }
  };

  return (
    <ErrorBoundary
      error={error}
      onRetry={() => {
        mutate(undefined, { revalidate: true });
      }}
    >
      {[
        {
          incidentStatusId: 0,
          statusName: "All (Default)",
        },
        ...statusOptions,
      ].map((o) => (
        <MenuItem
          key={o.incidentStatusId}
          onClick={(e) => {
            handleClick(o);
            props.onClick(e);
          }}
        >
          <ListItemText>{o.statusName}</ListItemText>
        </MenuItem>
      ))}
    </ErrorBoundary>
  );
});

const BuildUpStatusCell = ({
  status,
  statusDateTime,
  actionByUserName,
  playerId,
}: {
  status: string;
  statusDateTime: string;
  actionByUserName: string | null;
  playerId: number;
}): JSX.Element => {
  const theme = useTheme();
  const canAccess = useCanAccess(AppPermissions.modify.antiFraud);
  const updateStatusTooltip = "Do not have permissions to update status.";
  const formatUsername = actionByUserName === "SYSTEM" ? "System" : actionByUserName;
  const location = useLocation();
  const emailPrefix = formatUsername?.split("@")[0];

  return (
    <Stack sx={{ py: 1, wordBreak: "break-word" }}>
      <SMTooltip title={canAccess ? "" : updateStatusTooltip}>
        <Box>
          <Link
            component={RouterLink}
            to={
              location.pathname === "/" || location.pathname.toLowerCase() === "/dashboard"
                ? Routes.buildUpStatusDashboardFor(playerId)
                : Routes.buildUpStatusPageFor(playerId)
            }
            sx={
              canAccess
                ? { cursor: "pointer" }
                : {
                    pointerEvents: "none",
                    color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.common.black,
                    textDecoration: "none",
                  }
            }
            aria-disabled={!canAccess}
            tabIndex={!canAccess ? -1 : undefined}
          >
            <Typography>{status}</Typography>
          </Link>
        </Box>
      </SMTooltip>
      <Typography>{formatDateTime(statusDateTime)}</Typography>
      {formatUsername && <Typography>Last updated by</Typography>}
      <SMTooltip title={formatUsername}>
        <Typography>{emailPrefix || ""}</Typography>
      </SMTooltip>
    </Stack>
  );
};

const FreePlayTodayCell = ({
  playerId,
  amountOfFreePlayToday,
}: {
  playerId: number;
  amountOfFreePlayToday: number;
}) => {
  const location = useLocation();

  return amountOfFreePlayToday === 0 ? (
    <Typography>{toCurrencyFormat(amountOfFreePlayToday)}</Typography>
  ) : (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography> {toCurrencyFormat(amountOfFreePlayToday)}</Typography>
      <SMTooltip title="View breakdown">
        <IconButton
          component={RouterLink}
          to={
            location.pathname === "/" || location.pathname.toLowerCase() === "/dashboard"
              ? Routes.freePlayTodayDashboardFor(playerId)
              : Routes.freePlayTodayPageFor(playerId)
          }
        >
          <MoreVertIcon />
        </IconButton>
      </SMTooltip>
    </Stack>
  );
};

const columns: GridColDef<IBuildUpPlayer>[] = [
  {
    field: "status",
    headerName: "Investigation Status",
    minWidth: 280,
    hideable: false,
    valueGetter: ({ value }) => value,
    renderCell: ({ row }) => <BuildUpStatusCell {...row} />,
  },
  {
    field: "playerName",
    headerName: "Player",
    flex: 1,
    minWidth: PLAYER_NAME_MIN_WIDTH,
    valueGetter: ({ value }) => value,
    renderCell: ({ row }) => (
      <Grid container>
        <Grid item container>
          <Grid item xs={2} alignItems={"center"}>
            {row.indicator && (
              <SMTooltip title="Played Advantage Game during current calendar day">
                <HdrAutoIcon color="error" />
              </SMTooltip>
            )}
          </Grid>
          <Grid item xs={10}>
            <ClickableTypography to={Routes.overviewPathFor(row.playerId)}>{row.playerName}</ClickableTypography>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={2}></Grid>
          <Grid item xs={10}>
            <Typography>{row.playerId}</Typography>
          </Grid>
        </Grid>
      </Grid>
    ),
  },

  {
    field: "flaggedDate",
    headerName: "Flagged Date / Reason",
    minWidth: 250,
    valueGetter: ({ value }) => new Date(value),
    type: "date",
    renderCell: ({ row }) => (
      <Stack>
        <Typography>{moment(row.flaggedDate).format("MM/DD/yyyy")}</Typography>
        <Typography>{row.qualifyingFlag}</Typography>
      </Stack>
    ),
  },
  {
    field: "flaggedCoinIn",
    headerName: "Flagged Coin-In",
    minWidth: 200,
    valueGetter: ({ value }) => value,
    valueFormatter: ({ value }) => toCurrencyFormat(value),
  },
  {
    field: "amountOfFreePlayToday",
    headerName: "Total Free Play Available Today",
    minWidth: 220,
    valueGetter: ({ value }) => value,
    renderCell: ({ row }) => <FreePlayTodayCell {...row} />,
  },
  {
    field: "amountOfTotalFreePlay",
    headerName: "Total Free Play Assigned",
    minWidth: 180,
    valueGetter: ({ value }) => value,
    valueFormatter: ({ value }) => toCurrencyFormat(value),
  },
  {
    field: "timeofActivity",
    headerName: "Activity Location",
    minWidth: 280,
    flex: 1,
    type: "dateTime",
    valueGetter: ({ value }) => new Date(value),
    renderCell: ({ row }) => {
      return (
        <Typography>
          {row.activityType} - {moment(new Date(row.timeofActivity)).format("h:mm A")}
          <br />
          {row.locationOfActivity}
        </Typography>
      );
    },
  },
];

export const BuildUpPlayersOnFloorTable = (props: GridPaginationProps): JSX.Element => {
  const response = useBuildUpPlayersOnFloor();
  const isMobile = useMobile();

  return (
    <>
      <SWRDataGrid
        name={"flagged-free-play"}
        response={response}
        columns={columns}
        noRowsMessage="No flagged free play players on Floor"
        slots={{
          columnMenu: CustomColumnMenu,
        }}
        slotProps={{
          filterPanel: isMobile ? filterPanelSx : undefined,
          columnMenu: {
            customizedcolumns: ["status"],
            customfilteritem: CustomBuildUpStatusFilterItem,
          },
        }}
        {...props}
      />
      <Outlet />
    </>
  );
};
